/**
 * Easy sign-in devices, as the office keeps them and hands them out.
 *
 * A device the office registered once signs its driver in with nothing typed, and that driver
 * signs in nowhere else. Two kinds. The firm's own phone or tablet, registered only from the
 * devices page with a QR code for nobody, stays in the firm's list, given and taken back,
 * until it is written off. A driver's own phone, registered from his QR code or his request,
 * is his alone: never free, never given to anybody else, and forgotten when taken back.
 * Every call is an administrator's; a dispatcher is refused.
 */

import { request, type Schema } from '@taxi/api-client';

/** A phone waiting for the office, with the code it shows on its screen. */
export type DeviceRequest = Schema<'DeviceRequestRow'>;

/** How one driver uses service devices, and which phones they have. */
export type DeviceDriver = Schema<'DeviceDriverView'>;

export type ServiceDevice = Schema<'ServiceDeviceView'>;

/** A device as the list shows it: the firm's or a driver's own, who has it, or that it is free. */
export type FirmDevice = Schema<'FirmDeviceView'>;

export type DeviceKind = 'Phone' | 'Tablet';

export type DeviceRetirement = 'Broken' | 'Stolen' | 'Lost';

export type DeviceTicket = Schema<'DeviceTicketView'>;

export type ApproveDevice = Schema<'ApproveDeviceBody'>;

export type NewDeviceDriver = Schema<'NewDeviceDriverBody'>;

const base = '/api/tenant/devices';

export const devicesApi = {
  requests: () => request<DeviceRequest[]>(`${base}/requests`),

  list: () => request<FirmDevice[]>(base),

  /** A QR code that registers the scanning device as the firm's, given to nobody yet. */
  freeTicket: (label: string | null) =>
    request<DeviceTicket>(`${base}/ticket`, { method: 'POST', body: { label } }),

  update: (deviceId: string, body: { label: string | null; kind: DeviceKind }) =>
    request<void>(`${base}/${deviceId}`, { method: 'PUT', body }),

  assign: (deviceId: string, driverId: string) =>
    request<void>(`${base}/${deviceId}/assign`, { method: 'POST', body: { driverId } }),

  /** Back to the firm, free: its session ends at once. */
  release: (deviceId: string) => request<void>(`${base}/${deviceId}/release`, { method: 'POST' }),

  retire: (deviceId: string, reason: DeviceRetirement) =>
    request<void>(`${base}/${deviceId}/retire`, { method: 'POST', body: { reason } }),
  /**
   * Forgets one: the firm's only when nobody has it, a driver's own at any time. It leaves the
   * list, and the device starts again from nothing.
   */
  remove: (deviceId: string) => request<void>(`${base}/${deviceId}`, { method: 'DELETE' }),

  approve: (requestId: string, body: ApproveDevice) =>
    request<Schema<'ApprovedDeviceView'>>(`${base}/requests/${requestId}/approve`, { method: 'POST', body }),

  reject: (requestId: string) =>
    request<void>(`${base}/requests/${requestId}/reject`, { method: 'POST' }),

  /** A driver with no address or password, who signs in on service devices. */
  createDriver: (body: NewDeviceDriver) =>
    request<Schema<'FleetDriver'>>(`${base}/drivers`, { method: 'POST', body }),

  driver: (driverId: string) => request<DeviceDriver>(`${base}/drivers/${driverId}`),

  setPolicy: (driverId: string, body: Schema<'DevicePolicyBody'>) =>
    request<DeviceDriver>(`${base}/drivers/${driverId}`, { method: 'PUT', body }),

  ticket: (driverId: string) =>
    request<DeviceTicket>(`${base}/drivers/${driverId}/ticket`, { method: 'POST' }),

  ticketState: (ticketId: string) =>
    request<Schema<'DeviceTicketState'>>(`${base}/tickets/${ticketId}`),
};
